import { buildTypedIncidentMarkerIconSvg } from "./live-map-incident-marker.icons";
import {
  DEFAULT_ORANGE_MARKER_COLOR,
  FIGMA_BADGE_FILL_BY_ICON_TYPE,
  ICON_TYPE_SET,
  INCIDENT_MARKER_PULSE_BORDER_ALPHA,
  INCIDENT_MARKER_PULSE_SHADOW_ALPHA,
  LIVE_MAP_INCIDENT_OPENED_HALO_ALPHA_CORE,
  LIVE_MAP_INCIDENT_OPENED_HALO_ALPHA_EDGE,
  LIVE_MAP_INCIDENT_OPENED_HALO_ALPHA_MID,
  LIVE_MAP_INCIDENT_OPENED_HALO_FADE_STOP_PERCENT,
  LIVE_MAP_INCIDENT_OPENED_RING_GLOW_ALPHA_INNER,
  LIVE_MAP_INCIDENT_OPENED_RING_GLOW_ALPHA_MID,
  LIVE_MAP_INCIDENT_OPENED_RING_GLOW_ALPHA_OUTER,
  LIVE_MAP_INCIDENT_OPENED_RING_GLOW_BLUR_INNER_PX,
  LIVE_MAP_INCIDENT_OPENED_RING_GLOW_BLUR_MID_PX,
  LIVE_MAP_INCIDENT_OPENED_RING_GLOW_BLUR_OUTER_PX,
} from "./live-map.incident-marker.constants";
import type {
  ILiveMapIncidentMarkerPresentation,
  TLiveMapIncidentMarkerIconType,
} from "./live-map.incident-marker.models";

const isKnownIconType = (
  value: string | null | undefined,
): value is TLiveMapIncidentMarkerIconType =>
  typeof value === "string" && ICON_TYPE_SET.has(value);

const normalizeHexColor = (
  color: string | null | undefined,
): string | undefined => {
  const trimmed = (color ?? "").trim();

  return /^#[0-9A-Fa-f]{3,8}$/.test(trimmed) ? trimmed : undefined;
};

const resolveIconType = (
  incidentTypeIconType?: string | null,
): TLiveMapIncidentMarkerIconType | undefined =>
  isKnownIconType(incidentTypeIconType) ? incidentTypeIconType : undefined;

export const resolveLiveMapIncidentMarkerPresentation = ({
  incidentTypeIconType,
  incidentTypeColor,
  hasOpenedPopupOnMap,
}: {
  incidentTypeIconType?: string | null;
  incidentTypeColor?: string | null;
  hasOpenedPopupOnMap: boolean;
}): ILiveMapIncidentMarkerPresentation => {
  const iconType = resolveIconType(incidentTypeIconType);

  if (!iconType) {
    const presentation: ILiveMapIncidentMarkerPresentation = {
      variant: "default",
      color:
        normalizeHexColor(incidentTypeColor) ?? DEFAULT_ORANGE_MARKER_COLOR,
      hasOpenedPopupOnMap,
    };
    return presentation;
  }

  const typeColor = normalizeHexColor(incidentTypeColor);
  const color = typeColor ?? FIGMA_BADGE_FILL_BY_ICON_TYPE[iconType];

  const presentation: ILiveMapIncidentMarkerPresentation = {
    variant: "typed",
    color,
    iconType,
    hasOpenedPopupOnMap,
  };
  return presentation;
};

const escapeHtmlAttr = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

const hexToRgba = (hex: string, alpha: number): string => {
  const raw = hex.replace("#", "").trim();

  const full =
    raw.length === 3
      ? raw
          .split("")
          .map(char => char + char)
          .join("")
      : raw.slice(0, 6);

  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const buildMarkerDotInlineStyle = (color: string): string =>
  escapeHtmlAttr(`background:${color}`);

const buildMarkerBadgeInlineStyle = (color: string): string =>
  escapeHtmlAttr(`color:${color}`);

const buildMarkerPulseInlineStyle = (
  color: string,
  variant: "unopened" | "opened",
): string => {
  if (variant === "opened") {
    const innerGlow = hexToRgba(
      color,
      LIVE_MAP_INCIDENT_OPENED_RING_GLOW_ALPHA_INNER,
    );
    const midGlow = hexToRgba(
      color,
      LIVE_MAP_INCIDENT_OPENED_RING_GLOW_ALPHA_MID,
    );
    const outerGlow = hexToRgba(
      color,
      LIVE_MAP_INCIDENT_OPENED_RING_GLOW_ALPHA_OUTER,
    );

    return escapeHtmlAttr(
      `border:2px solid ${color};filter:drop-shadow(0 0 ${LIVE_MAP_INCIDENT_OPENED_RING_GLOW_BLUR_INNER_PX}px ${innerGlow}) drop-shadow(0 0 ${LIVE_MAP_INCIDENT_OPENED_RING_GLOW_BLUR_MID_PX}px ${midGlow}) drop-shadow(0 0 ${LIVE_MAP_INCIDENT_OPENED_RING_GLOW_BLUR_OUTER_PX}px ${outerGlow})`,
    );
  }

  return escapeHtmlAttr(
    `border:2px solid ${hexToRgba(color, INCIDENT_MARKER_PULSE_BORDER_ALPHA)};filter:drop-shadow(0 0 5px ${hexToRgba(color, INCIDENT_MARKER_PULSE_SHADOW_ALPHA)}) drop-shadow(0 0 9px ${hexToRgba(color, INCIDENT_MARKER_PULSE_SHADOW_ALPHA)})`,
  );
};

const buildOpenedHaloHtml = (color: string): string => {
  const gradient = escapeHtmlAttr(
    `background:radial-gradient(circle,${hexToRgba(color, LIVE_MAP_INCIDENT_OPENED_HALO_ALPHA_CORE)} 0%,${hexToRgba(color, LIVE_MAP_INCIDENT_OPENED_HALO_ALPHA_MID)} 38%,${hexToRgba(color, LIVE_MAP_INCIDENT_OPENED_HALO_ALPHA_EDGE)} 58%,transparent ${LIVE_MAP_INCIDENT_OPENED_HALO_FADE_STOP_PERCENT}%)`,
  );

  return `<div class="ryde-live-map-incident-marker__halo" style="${gradient}"></div>`;
};

const wrapIncidentMarkerInnerHtml = ({
  color,
  hasOpenedPopupOnMap,
  pulseStyle,
  bodyHtml,
}: {
  color: string;
  hasOpenedPopupOnMap: boolean;
  pulseStyle: string;
  bodyHtml: string;
}): string => {
  const haloHtml = hasOpenedPopupOnMap ? buildOpenedHaloHtml(color) : "";

  return `${haloHtml}<div class="ryde-live-map-incident-marker__pulse" style="${pulseStyle}"></div>${bodyHtml}`;
};

const wrapIncidentMarkerHtmlInIconCanvas = (markerHtml: string): string =>
  `<div class="ryde-live-map-incident-marker-canvas">${markerHtml}</div>`;

const buildIncidentMarkerRootClass = ({
  isTyped,
  hasOpenedPopupOnMap,
}: {
  isTyped: boolean;
  hasOpenedPopupOnMap: boolean;
}): string => {
  const classes = ["ryde-live-map-incident-marker"];

  if (isTyped) {
    classes.push("ryde-live-map-incident-marker--typed");
  }

  if (hasOpenedPopupOnMap) {
    classes.push("ryde-live-map-incident-marker--opened");
  } else {
    classes.push("ryde-live-map-incident-marker--unopened");

    if (isTyped) {
      classes.push("ryde-live-map-incident-marker--unopened-typed");
    }
  }

  return classes.join(" ");
};

export const buildTypedIncidentMarkerHtml = (
  presentation: ILiveMapIncidentMarkerPresentation & {
    iconType: TLiveMapIncidentMarkerIconType;
  },
): string => {
  const rootClass = buildIncidentMarkerRootClass({
    isTyped: true,
    hasOpenedPopupOnMap: presentation.hasOpenedPopupOnMap,
  });
  const pulseVariant = presentation.hasOpenedPopupOnMap ? "opened" : "unopened";
  const pulseStyle = buildMarkerPulseInlineStyle(
    presentation.color,
    pulseVariant,
  );
  const badgeStyle = buildMarkerBadgeInlineStyle(presentation.color);
  const iconHtml = buildTypedIncidentMarkerIconSvg(presentation.iconType);

  const innerHtml = wrapIncidentMarkerInnerHtml({
    color: presentation.color,
    hasOpenedPopupOnMap: presentation.hasOpenedPopupOnMap,
    pulseStyle,
    bodyHtml: `<div class="ryde-live-map-incident-marker__badge" style="${badgeStyle}">${iconHtml}</div>`,
  });

  return wrapIncidentMarkerHtmlInIconCanvas(
    `<div class="${rootClass}">${innerHtml}</div>`,
  );
};

export const buildDefaultIncidentMarkerHtml = ({
  color,
  hasOpenedPopupOnMap,
}: {
  color: string;
  hasOpenedPopupOnMap: boolean;
}): string => {
  const rootClass = buildIncidentMarkerRootClass({
    isTyped: false,
    hasOpenedPopupOnMap,
  });

  const dotStyle = buildMarkerDotInlineStyle(color);
  const pulseVariant = hasOpenedPopupOnMap ? "opened" : "unopened";
  const pulseStyle = buildMarkerPulseInlineStyle(color, pulseVariant);

  const innerHtml = wrapIncidentMarkerInnerHtml({
    color,
    hasOpenedPopupOnMap,
    pulseStyle,
    bodyHtml: `<div class="ryde-live-map-incident-marker__dot" style="${dotStyle}"></div>`,
  });

  return wrapIncidentMarkerHtmlInIconCanvas(
    `<div class="${rootClass}">${innerHtml}</div>`,
  );
};
